import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Download, CalendarCheck, MessageCircle, FileText } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDownloads } from "@/hooks/useData";
import { DOWNLOAD_TYPE } from "@/lib/constants";
import BrochureLeadDialog from "@/components/shared/BrochureLeadDialog";
import PriceListLeadDialog from "@/components/shared/PriceListLeadDialog";
import WhatsAppLeadDialog from "@/components/shared/WhatsAppLeadDialog";
import { requestWhatsApp } from "@/lib/whatsapp";

const CYCLE = [Download, CalendarCheck, MessageCircle, FileText];

export default function FloatingActionButton() {
    const [open, setOpen] = useState(false);
    const [brochureOpen, setBrochureOpen] = useState(false);
    const [pricelistOpen, setPricelistOpen] = useState(false);
    const [whatsappOpen, setWhatsappOpen] = useState(false);
    const [iconIdx, setIconIdx] = useState(0);
    const [scrolled, setScrolled] = useState(false);
    const { downloads } = useDownloads();
    const brochure = downloads.find((d) => d.type === DOWNLOAD_TYPE.BROCHURE);
    const pricelist = downloads.find((d) => d.type === DOWNLOAD_TYPE.PRICELIST);

    // Delay FAB until the user scrolls past the hero.
    useEffect(() => {
        const onScroll = () => {
            const visible = window.scrollY > 400;
            setScrolled(visible);
            if (!visible) setOpen(false);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Collapsed icon slowly rotates between actions to hint what's inside.
    useEffect(() => {
        if (open) return;
        const id = setInterval(() => setIconIdx((i) => (i + 1) % CYCLE.length), 2200);
        return () => clearInterval(id);
    }, [open]);

    const openDownload = (item) => {
        if (!item) return;
        setOpen(false);
        if (item.type === DOWNLOAD_TYPE.BROCHURE) {
            setBrochureOpen(true);
            return;
        }
        if (item.type === DOWNLOAD_TYPE.PRICELIST) {
            setPricelistOpen(true);
        }
    };

    const actions = [
        { key: "brochure", label: "Download Brochure", icon: Download, onClick: () => openDownload(brochure), disabled: !brochure },
        { key: "pricelist", label: "Price List", icon: FileText, onClick: () => openDownload(pricelist), disabled: !pricelist },
        { key: "visit", label: "Book a Visit", icon: CalendarCheck, to: "/contact" },
        {
            key: "whatsapp",
            label: "WhatsApp",
            icon: MessageCircle,
            onClick: () => {
                setOpen(false);
                requestWhatsApp({ openDialog: () => setWhatsappOpen(true) });
            },
        },
    ];

    const ActiveIcon = CYCLE[iconIdx];

    return (
        <div
            className={`fixed bottom-6 right-6 z-50 flex flex-col items-end gap-4 transition-all duration-500 ${scrolled ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"}`}
            data-testid="fab"
            data-visible={scrolled ? "true" : "false"}
        >
            <AnimatePresence>
                {open && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-end gap-3.5">
                        {actions.map((a, i) => {
                            const inner = (
                                <>
                                    <span className="rounded-full border border-brand-beige bg-brand-warm/95 px-5 py-2.5 font-sans text-[0.95rem] tracking-wide text-brand-ink shadow-[0_8px_30px_rgba(74,69,63,0.12)] backdrop-blur">
                                        {a.label}
                                    </span>
                                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-brand-ink shadow-[0_8px_24px_rgba(74,69,63,0.16)] transition-colors duration-300 group-hover:bg-brand-gold group-hover:text-white">
                                        <a.icon className="h-5 w-5" />
                                    </span>
                                </>
                            );
                            const cls = "group flex items-center gap-3.5";
                            const mp = {
                                initial: { opacity: 0, y: 12 },
                                animate: { opacity: 1, y: 0 },
                                exit: { opacity: 0, y: 12 },
                                transition: { duration: 0.28, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
                            };
                            if (a.to) return <motion.div key={a.key} {...mp}><Link to={a.to} onClick={() => setOpen(false)} data-testid={`fab-${a.key}`} className={cls}>{inner}</Link></motion.div>;
                            if (a.href) return <motion.div key={a.key} {...mp}><a href={a.href} target="_blank" rel="noreferrer" onClick={() => { a.onClick?.(); setOpen(false); }} data-testid={`fab-${a.key}`} className={cls}>{inner}</a></motion.div>;
                            return <motion.div key={a.key} {...mp}><button type="button" onClick={a.onClick} disabled={a.disabled} data-testid={`fab-${a.key}`} className={`${cls}${a.disabled ? " opacity-50 pointer-events-none" : ""}`}>{inner}</button></motion.div>;
                        })}
                    </motion.div>
                )}
            </AnimatePresence>

            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="fab-toggle"
                aria-label={open ? "Close menu" : "Open contact menu"}
                className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-gold text-white shadow-[0_10px_40px_rgba(198,134,43,0.45)] transition-transform duration-300 hover:scale-105"
            >
                <AnimatePresence mode="wait">
                    {open ? (
                        <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.25 }}>
                            <X className="h-6 w-6" />
                        </motion.span>
                    ) : (
                        <motion.span key={iconIdx} initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -8, opacity: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
                            <ActiveIcon className="h-6 w-6" />
                        </motion.span>
                    )}
                </AnimatePresence>
            </button>
            <BrochureLeadDialog
                download={brochure}
                open={brochureOpen}
                onOpenChange={setBrochureOpen}
            />
            <PriceListLeadDialog
                download={pricelist}
                open={pricelistOpen}
                onOpenChange={setPricelistOpen}
            />
            <WhatsAppLeadDialog
                open={whatsappOpen}
                onOpenChange={setWhatsappOpen}
            />
        </div>
    );
}
